import { Button, Cartel, Checkbox, IconButton, InventoryNumber, Price, Select } from '@artosera/ui';
import type { StatutOeuvre } from '@artosera/shared';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDesign } from '../../appearance/useDesign.js';
import { DossierOeuvresDialog } from './DossierOeuvresDialog.js';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useArtistes } from '../../api/artistes.js';
import { documentsGalerieApi } from '../../api/documentsGalerie.js';
import { useLieux } from '../../api/lieux.js';
import { useApercuMedia } from '../../api/medias.js';
import { useOeuvre, useOeuvres, type FiltresOeuvres } from '../../api/oeuvres.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { ImportExportDialog } from './ImportExportDialog.js';
import { NouvelleOeuvreDialog } from './NouvelleOeuvreDialog.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { STATUTS_TRIES } from './statutUi.js';
import { useDebounce } from './useDebounce.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { BasculeVue } from '../../composants/BasculeVue.js';
import { CarteOeuvre } from './CarteOeuvre.js';
import { MenuTri } from './MenuTri.js';
import { BasculeTaille } from '../../composants/BasculeTaille.js';
import { useTaille } from '../../preferences/tailleStorage.js';
import { BandeauSelection } from './lot/BandeauSelection.js';
import { useVue } from '../../preferences/vueStorage.js';
import barre from './BarreOeuvres.module.css';
import styles from './OeuvresPage.module.css';

/** Un filtre actif, rappelé en puce dans la barre ; `retirer` le désactive. */
interface PuceFiltre {
  cle: string;
  libelle: string;
  retirer: () => void;
}

function selectionTransmise(etat: unknown): string[] {
  const ids = (etat as { selectionScan?: unknown } | null)?.selectionScan;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * Œuvres — liste ou cartes + fiche ouverte (3.3, écran 1, S3-a). Une barre
 * dense (recherche, filtres en panneau, tri avec sens, vue), pagination,
 * densité Registre, navigation clavier (flèches/Entrée/Échap), colonne prix
 * artiste seulement si l'API la rend. La liste est la vue par défaut dans tous
 * les designs ; vue et taille des vignettes sont mémorisées par compte.
 */
export function OeuvresPage(): JSX.Element {
  const { t, i18n } = useTranslation(['oeuvres', 'regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { design } = useDesign();
  // Structure a sa feuille de style propre, écrite sur les repères
  // `inventory-*` ; les autres designs habillent la liste par les repères
  // `list-*` (appearance/liste-oeuvres.css).
  const repere = (nom: string): string => `${design === 'structure' ? 'inventory' : 'list'}-${nom}`;
  const { galerieSlug, galerie, compte } = useShellContext();
  // La liste est la vue par défaut dans tous les designs ; le choix déjà
  // mémorisé par la personne reste prioritaire.
  const [vue, setVue] = useVue('oeuvres', compte.id, 'liste');
  const [taille, setTaille] = useTaille(compte.id);
  const [searchParams, setSearchParams] = useSearchParams();

  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [statut, setStatut] = useState('');
  const [artisteId, setArtisteId] = useState('');
  const [localisationId, setLocalisationId] = useState('');
  const [visibleSurSite, setVisibleSurSite] = useState(searchParams.get('visibleSurSite') ?? '');
  const incompletes = searchParams.get('incompletes') === 'site';
  const filtrerIncompletes = (actif: boolean): void => {
    setSearchParams((avant) => {
      const suite = new URLSearchParams(avant);
      if (actif) suite.set('incompletes', 'site');
      else suite.delete('incompletes');
      return suite;
    });
  };
  const [prixMin, setPrixMin] = useState('');
  const [prixMax, setPrixMax] = useState('');
  const [annee, setAnnee] = useState('');
  const [inclureArchivees, setInclureArchivees] = useState(false);
  const [tri, setTri] = useState<FiltresOeuvres['tri']>('recent');
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const [page, setPage] = useState(1);
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [importExportOuvert, setImportExportOuvert] = useState(false);
  // Une sélection transmise par la navigation (panier du scan, inventaire) ouvre la liste cochée.
  const emplacement = useLocation();
  const etatNavigation: unknown = emplacement.state;
  const [selectionListePrix, setSelectionListePrix] = useState<Set<string>>(
    () => new Set(selectionTransmise(etatNavigation)),
  );
  // Consommée : un retour arrière ou un rechargement ne recoche pas le panier d'origine.
  useEffect(() => {
    if (selectionTransmise(etatNavigation).length > 0)
      navigate(`${emplacement.pathname}${emplacement.search}`, { replace: true, state: null });
    // Une seule fois, à l'arrivée.
  }, []);
  const [dossierOuvert, setDossierOuvert] = useState(searchParams.get('dossier') === '1');
  const [listeDePrixEnCours, setListeDePrixEnCours] = useState(false);
  const [erreurListeDePrix, setErreurListeDePrix] = useState<string | null>(null);

  const filtres: FiltresOeuvres = useMemo(
    () => ({
      ...(incompletes ? { incompletes: 'site' as const } : {}),
      vignettes: true,
      ...(rechercheDebattue ? { q: rechercheDebattue } : {}),
      ...(statut ? { statut: [statut as StatutOeuvre] } : {}),
      ...(artisteId ? { artisteId } : {}),
      ...(localisationId ? { localisationId } : {}),
      ...(visibleSurSite ? { visibleSurSite: visibleSurSite === 'true' } : {}),
      ...(prixMin ? { prixMin: Math.round(Number(prixMin) * 100) } : {}),
      ...(prixMax ? { prixMax: Math.round(Number(prixMax) * 100) } : {}),
      ...(annee ? { annee: Number(annee) } : {}),
      inclureArchivees,
      tri,
      page,
      parPage: 50,
    }),
    [
      incompletes,
      rechercheDebattue,
      statut,
      artisteId,
      localisationId,
      visibleSurSite,
      prixMin,
      prixMax,
      annee,
      inclureArchivees,
      tri,
      page,
    ],
  );

  // Retour à la page 1 quand un filtre change : ajusté pendant le rendu
  // (React 19 refuse le `setState` synchrone dans un effet pour ce cas
  // d'« état dérivé d'une prop qui change », cf. « You Might Not Need an
  // Effect »), pas après coup dans un effet.
  const cleFiltresSansPage = JSON.stringify([
    incompletes,
    rechercheDebattue,
    statut,
    artisteId,
    localisationId,
    visibleSurSite,
    prixMin,
    prixMax,
    annee,
    inclureArchivees,
  ]);
  const [cleFiltresPrecedente, setCleFiltresPrecedente] = useState(cleFiltresSansPage);
  if (cleFiltresSansPage !== cleFiltresPrecedente) {
    setCleFiltresPrecedente(cleFiltresSansPage);
    if (page !== 1) setPage(1);
  }

  const filtresActifs = [
    incompletes ? 'site' : '',
    statut,
    artisteId,
    localisationId,
    visibleSurSite,
    prixMin,
    prixMax,
    annee,
    inclureArchivees ? 'oui' : '',
  ].filter(Boolean).length;
  const liste = useOeuvres(galerieSlug, filtres);
  const artistes = useArtistes(galerieSlug, { tri: 'nom', parPage: 200 });
  const lieux = useLieux(galerieSlug);

  // Les filtres actifs, rappelés en puces sous la barre (chacune se retire d'un clic).
  const puces: PuceFiltre[] = [];
  if (incompletes)
    puces.push({
      cle: 'incompletes',
      libelle: t('regie:oeuvres.completude.incomplete'),
      retirer: () => filtrerIncompletes(false),
    });
  if (statut) {
    puces.push({
      cle: 'statut',
      libelle: t(`oeuvres:statuts.${statut}`),
      retirer: () => setStatut(''),
    });
  }
  if (artisteId) {
    puces.push({
      cle: 'artiste',
      libelle:
        artistes.data?.artistes.find((artiste) => artiste.id === artisteId)?.nom ??
        t('regie:oeuvres.filtres.artiste'),
      retirer: () => setArtisteId(''),
    });
  }
  if (localisationId) {
    puces.push({
      cle: 'localisation',
      libelle:
        lieux.data?.find((lieu) => lieu.id === localisationId)?.nom ??
        t('regie:oeuvres.filtres.localisation'),
      retirer: () => setLocalisationId(''),
    });
  }
  if (visibleSurSite) {
    puces.push({
      cle: 'visibleSurSite',
      libelle: t(
        visibleSurSite === 'true'
          ? 'regie:oeuvres.barre.puceVisible'
          : 'regie:oeuvres.barre.puceMasquee',
      ),
      retirer: () => setVisibleSurSite(''),
    });
  }
  if (prixMin) {
    puces.push({
      cle: 'prixMin',
      libelle: t('regie:oeuvres.barre.pucePrixMin', { valeur: prixMin }),
      retirer: () => setPrixMin(''),
    });
  }
  if (prixMax) {
    puces.push({
      cle: 'prixMax',
      libelle: t('regie:oeuvres.barre.pucePrixMax', { valeur: prixMax }),
      retirer: () => setPrixMax(''),
    });
  }
  if (annee) {
    puces.push({
      cle: 'annee',
      libelle: t('regie:oeuvres.barre.puceAnnee', { valeur: annee }),
      retirer: () => setAnnee(''),
    });
  }
  if (inclureArchivees) {
    puces.push({
      cle: 'archivees',
      libelle: t('regie:oeuvres.barre.puceArchivees'),
      retirer: () => setInclureArchivees(false),
    });
  }

  const oeuvres = liste.data?.oeuvres ?? [];
  const selection = useOeuvre(galerieSlug, selectionId);
  const mediaPrincipal = selection.data?.medias.find(
    (media) => media.id === selection.data.oeuvre.mediaPrincipalId,
  );
  const apercu = useApercuMedia(galerieSlug, mediaPrincipal?.id ?? null);

  const nouvelleOuverte = searchParams.get('nouvelle') === '1';
  function fermerNouvelle(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.delete('nouvelle');
    setSearchParams(suivants, { replace: true });
  }

  function surClavier(event: KeyboardEvent<HTMLDivElement>): void {
    if (oeuvres.length === 0) return;
    const indexCourant = oeuvres.findIndex((oeuvre) => oeuvre.id === selectionId);
    // En cartes, la grille se parcourt aussi de gauche à droite.
    const vaVersLaSuivante =
      event.key === 'ArrowDown' || (vue === 'cartes' && event.key === 'ArrowRight');
    const vaVersLaPrecedente =
      event.key === 'ArrowUp' || (vue === 'cartes' && event.key === 'ArrowLeft');
    if (vaVersLaSuivante) {
      event.preventDefault();
      const suivante = oeuvres[Math.min(oeuvres.length - 1, indexCourant + 1)];
      if (suivante) setSelectionId(suivante.id);
    } else if (vaVersLaPrecedente) {
      event.preventDefault();
      const precedente = oeuvres[Math.max(0, indexCourant - 1)];
      if (precedente) setSelectionId(precedente.id);
    } else if (event.key === 'Enter') {
      if (selectionId) navigate(`/${galerieSlug}/oeuvres/${selectionId}`);
    } else if (event.key === 'Escape') {
      setSelectionId(null);
    }
  }

  function reinitialiserFiltres(): void {
    filtrerIncompletes(false);
    setRecherche('');
    setStatut('');
    setArtisteId('');
    setLocalisationId('');
    setVisibleSurSite('');
    setPrixMin('');
    setPrixMax('');
    setAnnee('');
    setInclureArchivees(false);
  }

  const peutImporter = galerieALaction(galerie, 'artworks:import');
  const peutExporter = galerieALaction(galerie, 'artworks:export');
  const peutDossier =
    galerieEcritModule(galerie, 'documents-prestige') &&
    galerieALaction(galerie, 'documents:write');
  const peutPresentation =
    galerieAModule(galerie, 'rendez-vous-client') &&
    galerieALaction(galerie, 'presentation:read') &&
    galerieALaction(galerie, 'presentation:write');
  const peutSelection =
    galerieALaction(galerie, 'artworks:write') ||
    peutPresentation ||
    peutDossier ||
    galerieALaction(galerie, 'exhibitions:price-list') ||
    (galerieAModule(galerie, 'catalogage-image') && galerie.role === 'direction');
  const peutListeDePrix = galerieALaction(galerie, 'exhibitions:price-list');
  // Droits visibles (ARTO-53, § 4) : le comptable n'a pas `artworks:write` —
  // même en forçant `?nouvelle=1` dans l'URL, le dialogue reste fermé.
  const peutCreer = galerieALaction(galerie, 'artworks:write');

  function basculerSelectionListePrix(id: string): void {
    setSelectionListePrix((avant) => {
      const apres = new Set(avant);
      if (apres.has(id)) apres.delete(id);
      else apres.add(id);
      return apres;
    });
  }

  const toutLaPageCochee =
    oeuvres.length > 0 && oeuvres.every((oeuvre) => selectionListePrix.has(oeuvre.id));

  function basculerToutLaPage(): void {
    setSelectionListePrix((avant) => {
      const toutesCochees = oeuvres.every((oeuvre) => avant.has(oeuvre.id));
      if (toutesCochees) {
        const apres = new Set(avant);
        oeuvres.forEach((oeuvre) => apres.delete(oeuvre.id));
        return apres;
      }
      const apres = new Set(avant);
      oeuvres.forEach((oeuvre) => apres.add(oeuvre.id));
      return apres;
    });
  }

  async function ouvrirListeDePrixSelection(): Promise<void> {
    setErreurListeDePrix(null);
    setListeDePrixEnCours(true);
    try {
      const document = await dansLaPorteeCourante((appel) =>
        documentsGalerieApi.listeDePrixSelectionPdf(
          galerieSlug,
          [...selectionListePrix],
          langue,
          appel,
        ),
      );
      ouvrirUrlSignee(document.url);
    } catch (error) {
      setErreurListeDePrix(
        estApiError(error) ? error.message : t('regie:oeuvres.listeDePrixSelection.echec'),
      );
    } finally {
      setListeDePrixEnCours(false);
    }
  }

  return (
    <div
      className={styles.page}
      data-ui="inventory"
      data-selection={selectionListePrix.size > 0 ? 'oui' : undefined}
    >
      {dossierOuvert && peutDossier && (
        <DossierOeuvresDialog
          open
          onOpenChange={(o) => {
            setDossierOuvert(o);
            if (!o && searchParams.has('dossier')) {
              const p = new URLSearchParams(searchParams);
              p.delete('dossier');
              setSearchParams(p);
            }
          }}
          oeuvreIds={
            selectionListePrix.size > 0
              ? [...selectionListePrix]
              : (searchParams.get('oeuvres') ?? '').split(',').filter(Boolean)
          }
        />
      )}
      <div className={styles.entete} data-ui="page-heading">
        <div className={styles.titreBloc}>
          <h1 className={styles.titre} data-ui="page-title">
            {t('oeuvres:titre')}
          </h1>
          {liste.data ? <span className={styles.compte}>{liste.data.pagination.total}</span> : null}
        </div>
        <div className={styles.acteEntete}>
          {peutImporter || peutExporter ? (
            <Button variant="secondary" size="sm" onClick={() => setImportExportOuvert(true)}>
              <FileUp size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:oeuvres.importExport.ouvrir')}
            </Button>
          ) : null}
        </div>
      </div>
      {erreurListeDePrix ? (
        <p className={styles.erreur} role="alert">
          {erreurListeDePrix}
        </p>
      ) : null}

      <div
        className={`${styles.filtres} ${barre.barre}`}
        role="search"
        aria-label={t('regie:oeuvres.filtres.titre')}
        data-ui="inventory-filters"
      >
        <div className={barre.ligne}>
          <div className={styles.champRecherche}>
            <Search size={16} strokeWidth={1.5} aria-hidden />
            <input
              type="search"
              className={styles.saisieRecherche}
              placeholder={t('regie:oeuvres.rechercheChamp')}
              aria-label={t('regie:oeuvres.rechercheChamp')}
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </div>

          <button
            type="button"
            className={barre.boutonFiltres}
            aria-expanded={filtresOuverts}
            aria-controls="oeuvres-filtres-panneau"
            data-actif={filtresActifs > 0 ? 'true' : undefined}
            onClick={() => setFiltresOuverts((valeur) => !valeur)}
          >
            <SlidersHorizontal size={16} strokeWidth={1.5} aria-hidden />
            <span className={barre.boutonTexte}>{t('regie:oeuvres.filtres.titre')}</span>
            {filtresActifs > 0 ? <span className={barre.compte}>{filtresActifs}</span> : null}
            <ChevronDown size={16} strokeWidth={1.5} className={barre.chevron} aria-hidden />
          </button>

          <MenuTri tri={tri} onChange={setTri} />
          <div className={styles.reglagesVue}>
            <BasculeTaille taille={taille} onChange={setTaille} />
            <BasculeVue vue={vue} onChange={setVue} />
          </div>
        </div>

        {puces.length > 0 ? (
          <ul className={barre.puces} aria-label={t('regie:oeuvres.barre.filtresActifs')}>
            {puces.map((puce) => (
              <li key={puce.cle}>
                <button
                  type="button"
                  className={barre.puce}
                  aria-label={t('regie:oeuvres.barre.retirer', { filtre: puce.libelle })}
                  onClick={puce.retirer}
                >
                  {puce.libelle}
                  <X size={14} strokeWidth={1.75} aria-hidden />
                </button>
              </li>
            ))}
            <li>
              <Button variant="ghost" size="sm" onClick={reinitialiserFiltres}>
                {t('regie:oeuvres.barre.toutEffacer')}
              </Button>
            </li>
          </ul>
        ) : null}

        {filtresOuverts ? (
          <div
            id="oeuvres-filtres-panneau"
            className={barre.panneau}
            role="group"
            aria-label={t('regie:oeuvres.filtres.titre')}
          >
            <Checkbox
              id="oeuvres-incompletes"
              label={t('regie:oeuvres.completude.incomplete')}
              checked={incompletes}
              onCheckedChange={(valeur) => filtrerIncompletes(valeur === true)}
            />
            <div className={barre.champ}>
              <span className={barre.etiquette} aria-hidden="true">
                {t('regie:oeuvres.filtres.statut')}
              </span>
              <Select
                aria-label={t('regie:oeuvres.filtres.statut')}
                value={statut}
                onValueChange={setStatut}
                placeholder={t('regie:oeuvres.filtres.tousLesStatuts')}
                options={[
                  { value: '', label: t('regie:oeuvres.filtres.tousLesStatuts') },
                  ...STATUTS_TRIES.map((code) => ({
                    value: code,
                    label: t(`oeuvres:statuts.${code}`),
                  })),
                ]}
              />
            </div>

            <div className={barre.champ}>
              <span className={barre.etiquette} aria-hidden="true">
                {t('regie:oeuvres.filtres.artiste')}
              </span>
              <Select
                aria-label={t('regie:oeuvres.filtres.artiste')}
                value={artisteId}
                onValueChange={setArtisteId}
                placeholder={t('regie:oeuvres.filtres.tousLesArtistes')}
                options={[
                  { value: '', label: t('regie:oeuvres.filtres.tousLesArtistes') },
                  ...(artistes.data?.artistes.map((artiste) => ({
                    value: artiste.id,
                    label: artiste.nom,
                  })) ?? []),
                ]}
              />
            </div>

            <div className={barre.champ}>
              <span className={barre.etiquette} aria-hidden="true">
                {t('regie:oeuvres.filtres.localisation')}
              </span>
              <Select
                aria-label={t('regie:oeuvres.filtres.localisation')}
                value={localisationId}
                onValueChange={setLocalisationId}
                placeholder={t('regie:oeuvres.filtres.toutesLesLocalisations')}
                options={[
                  { value: '', label: t('regie:oeuvres.filtres.toutesLesLocalisations') },
                  ...(lieux.data?.map((lieu) => ({ value: lieu.id, label: lieu.nom })) ?? []),
                ]}
              />
            </div>

            <div className={barre.champ}>
              <span className={barre.etiquette} aria-hidden="true">
                {t('regie:oeuvres.filtres.visibleSurSite')}
              </span>
              <Select
                aria-label={t('regie:oeuvres.filtres.visibleSurSite')}
                value={visibleSurSite}
                onValueChange={setVisibleSurSite}
                placeholder={t('regie:oeuvres.filtres.visibleSurSiteToutes')}
                options={[
                  { value: '', label: t('regie:oeuvres.filtres.visibleSurSiteToutes') },
                  { value: 'true', label: t('regie:oeuvres.filtres.visibleSurSiteOui') },
                  { value: 'false', label: t('regie:oeuvres.filtres.visibleSurSiteNon') },
                ]}
              />
            </div>

            <label className={barre.champ}>
              <span className={barre.etiquette}>{t('regie:oeuvres.filtres.prixMin')}</span>
              <input
                type="number"
                className={barre.saisieNombre}
                value={prixMin}
                onChange={(event) => setPrixMin(event.target.value)}
              />
            </label>
            <label className={barre.champ}>
              <span className={barre.etiquette}>{t('regie:oeuvres.filtres.prixMax')}</span>
              <input
                type="number"
                className={barre.saisieNombre}
                value={prixMax}
                onChange={(event) => setPrixMax(event.target.value)}
              />
            </label>
            <label className={barre.champ}>
              <span className={barre.etiquette}>{t('regie:oeuvres.filtres.annee')}</span>
              <input
                type="number"
                className={barre.saisieNombre}
                value={annee}
                onChange={(event) => setAnnee(event.target.value)}
              />
            </label>

            <div className={barre.caseArchivees}>
              <Checkbox
                id="oeuvres-archivees"
                label={t('regie:oeuvres.filtres.inclureArchivees')}
                checked={inclureArchivees}
                onCheckedChange={(valeur) => setInclureArchivees(valeur === true)}
              />
            </div>

            <div className={barre.pied}>
              <Button variant="ghost" size="sm" onClick={reinitialiserFiltres}>
                {t('regie:oeuvres.filtres.reinitialiser')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className={styles.corps}
        data-taille={taille}
        data-fiche={selection.data ? 'ouverte' : undefined}
        data-ui="inventory-body"
      >
        <section className={styles.liste} aria-label={t('oeuvres:titre')} data-ui="inventory-list">
          {peutSelection && selectionListePrix.size > 0 ? (
            <BandeauSelection
              selection={[...selectionListePrix]}
              idsPage={oeuvres.map((oeuvre) => oeuvre.id)}
              totalRecherche={liste.data?.pagination.total ?? oeuvres.length}
              filtres={filtres}
              onRemplacer={(ids) => setSelectionListePrix(new Set(ids))}
              onDeselectionner={() => setSelectionListePrix(new Set())}
              onDossier={
                peutDossier && selectionListePrix.size <= 30
                  ? () => setDossierOuvert(true)
                  : undefined
              }
              onListeDePrix={peutListeDePrix ? () => void ouvrirListeDePrixSelection() : undefined}
              listeDePrixEnCours={listeDePrixEnCours}
            />
          ) : null}
          {vue === 'cartes' && peutSelection && oeuvres.length > 0 ? (
            // Les cartes n'ont pas d'en-tête : « Tout sélectionner » vit ici.
            <div className={styles.selectionBarre} data-ui="inventory-select-all">
              <Checkbox
                label={t('regie:oeuvres.listeDePrixSelection.toutSelectionner')}
                checked={toutLaPageCochee}
                onCheckedChange={basculerToutLaPage}
              />
            </div>
          ) : null}
          {liste.isLoading ? (
            <p className={styles.vide}>{t('commun:chargement')}</p>
          ) : liste.isError ? (
            <p className={styles.vide} role="alert">
              {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
            </p>
          ) : oeuvres.length === 0 ? (
            <p className={styles.vide}>{t('regie:oeuvres.aucunResultat')}</p>
          ) : vue === 'cartes' ? (
            <div className={styles.cartesEnveloppe} tabIndex={0} onKeyDown={surClavier}>
              <ul className={styles.cartes}>
                {oeuvres.map((oeuvre) => (
                  <CarteOeuvre
                    key={oeuvre.id}
                    oeuvre={oeuvre}
                    vignetteUrl={liste.data?.vignettes?.urls[oeuvre.id] ?? null}
                    langue={langue}
                    active={oeuvre.id === selectionId}
                    cochee={peutSelection ? selectionListePrix.has(oeuvre.id) : null}
                    prixArtisteVisible={liste.data?.prixArtisteVisible === true}
                    onOuvrir={() => setSelectionId(oeuvre.id)}
                    onBasculerCase={() => basculerSelectionListePrix(oeuvre.id)}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <div
              className={styles.tableauEnveloppe}
              tabIndex={0}
              onKeyDown={surClavier}
              data-ui={repere('scroll')}
            >
              <table
                role="table"
                className={styles.tableau}
                data-density="registre"
                data-ui={repere('table')}
              >
                <thead role="rowgroup">
                  <tr role="row">
                    {peutSelection ? (
                      <th role="columnheader" scope="col" className={styles.celluleCase}>
                        <Checkbox
                          label=""
                          aria-label={t('regie:oeuvres.listeDePrixSelection.toutSelectionner')}
                          checked={toutLaPageCochee}
                          onCheckedChange={basculerToutLaPage}
                        />
                      </th>
                    ) : null}
                    <th role="columnheader" scope="col">
                      {t('regie:oeuvres.colonneOeuvre')}
                    </th>
                    <th role="columnheader" scope="col">
                      {t('regie:oeuvres.colonneArtiste')}
                    </th>
                    <th role="columnheader" scope="col">
                      {t('regie:oeuvres.colonneStatut')}
                    </th>
                    <th role="columnheader" scope="col">
                      {t('regie:oeuvres.colonneLocalisation')}
                    </th>
                    <th role="columnheader" scope="col" className={styles.cellulePrix}>
                      {t('regie:oeuvres.colonnePrix')}
                    </th>
                    {liste.data?.prixArtisteVisible ? (
                      <th role="columnheader" scope="col" className={styles.cellulePrixArtiste}>
                        {t('oeuvres:champs.prixArtiste')}
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {oeuvres.map((oeuvre) => (
                    <tr
                      role="row"
                      key={oeuvre.id}
                      data-selected={oeuvre.id === selectionId}
                      className={oeuvre.id === selectionId ? styles.ligneActive : ''}
                      data-ui={repere('row')}
                      data-active={oeuvre.id === selectionId ? 'true' : undefined}
                    >
                      {peutSelection ? (
                        <td
                          role="cell"
                          data-ui={repere('selection')}
                          className={styles.celluleCase}
                        >
                          <Checkbox
                            label=""
                            aria-label={t('regie:oeuvres.listeDePrixSelection.selectionnerOeuvre', {
                              titre: oeuvre.titre,
                            })}
                            checked={selectionListePrix.has(oeuvre.id)}
                            onCheckedChange={() => basculerSelectionListePrix(oeuvre.id)}
                          />
                        </td>
                      ) : null}
                      <td role="cell" className={styles.celluleOeuvre}>
                        <button
                          type="button"
                          className={styles.boutonLigne}
                          data-ui={repere('open')}
                          onClick={() => setSelectionId(oeuvre.id)}
                          aria-label={t('regie:oeuvres.ouvrirFiche', { titre: oeuvre.titre })}
                        >
                          <VignetteOeuvre
                            data-ui={repere('thumbnail')}
                            url={liste.data?.vignettes?.urls[oeuvre.id] ?? null}
                          />
                          <span className={styles.cartelLigne}>
                            <span className={styles.titreOeuvre} data-ui={repere('title')}>
                              {oeuvre.titre}
                              {(oeuvre.manques?.length ?? 0) > 0 ? (
                                <small
                                  className={styles.ficheIncomplete}
                                  title={t('regie:oeuvres.completude.titre')}
                                >
                                  {' '}
                                  · {t('regie:oeuvres.completude.incomplete')}
                                </small>
                              ) : null}
                            </span>
                            <InventoryNumber value={oeuvre.numeroInventaire} />
                          </span>
                        </button>
                      </td>
                      <td role="cell" className={styles.celluleArtiste} data-ui={repere('artist')}>
                        {oeuvre.artisteNom}
                      </td>
                      <td role="cell" className={styles.celluleStatut} data-ui={repere('status')}>
                        <PastilleStatut statut={oeuvre.statut} />
                      </td>
                      <td role="cell" className={styles.celluleLieu} data-ui={repere('location')}>
                        {lieux.data?.find((lieu) => lieu.id === oeuvre.localisationId)?.nom ?? '—'}
                      </td>
                      <td
                        role="cell"
                        data-ui={repere('price')}
                        data-label={t('regie:oeuvres.colonnePrix')}
                        className={styles.cellulePrix}
                      >
                        {oeuvre.prixPublicCentimes === null ? (
                          '—'
                        ) : (
                          <Price
                            cents={oeuvre.prixPublicCentimes}
                            lang={langue}
                            currency={oeuvre.devise}
                          />
                        )}
                      </td>
                      {liste.data?.prixArtisteVisible ? (
                        <td
                          role="cell"
                          className={styles.cellulePrixArtiste}
                          data-ui={repere('price')}
                          data-label={t('oeuvres:champs.prixArtiste')}
                          data-libelle={t('oeuvres:champs.prixArtiste')}
                        >
                          {oeuvre.prixArtisteCentimes ? (
                            <Price
                              cents={oeuvre.prixArtisteCentimes}
                              lang={langue}
                              currency={oeuvre.devise}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {liste.data && liste.data.pagination.pages > 1 ? (
            <div className={styles.pagination}>
              <IconButton
                variant="outline"
                icon={<ChevronLeft aria-hidden />}
                aria-label={t('regie:oeuvres.pagination.precedent')}
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
              />
              <span>
                {t('regie:oeuvres.pagination.page', {
                  page: liste.data.pagination.page,
                  pages: liste.data.pagination.pages,
                })}
              </span>
              <IconButton
                variant="outline"
                icon={<ChevronRight aria-hidden />}
                aria-label={t('regie:oeuvres.pagination.suivant')}
                size="sm"
                disabled={page >= liste.data.pagination.pages}
                onClick={() =>
                  setPage((valeur) => Math.min(liste.data!.pagination.pages, valeur + 1))
                }
              />
            </div>
          ) : null}
        </section>

        {selection.data ? (
          <aside
            className={styles.fiche}
            aria-label={t('regie:oeuvres.cartel')}
            data-ui="inventory-inspector"
          >
            <IconButton
              className={styles.fermerFiche}
              icon={<X aria-hidden />}
              aria-label={t('regie:oeuvres.fermerFiche')}
              onClick={() => setSelectionId(null)}
            />
            <div
              className={styles.planche}
              data-ui={apercu.data ? undefined : 'inventory-preview-empty'}
              data-vide={apercu.data ? undefined : 'true'}
            >
              {apercu.data ? (
                <img
                  className={styles.image}
                  src={apercu.data}
                  alt=""
                  data-ui="inventory-preview"
                />
              ) : null}
            </div>
            <Cartel
              artist={selection.data.oeuvre.artisteNom}
              title={selection.data.oeuvre.titre}
              year={selection.data.oeuvre.annee ?? undefined}
              medium={
                selection.data.oeuvre.technique?.[langue] ?? selection.data.oeuvre.technique?.fr
              }
              dimensions={
                selection.data.oeuvre.dimensions
                  ? {
                      height: selection.data.oeuvre.dimensions.hauteur,
                      width: selection.data.oeuvre.dimensions.largeur,
                      ...(selection.data.oeuvre.dimensions.profondeur === undefined
                        ? {}
                        : { depth: selection.data.oeuvre.dimensions.profondeur }),
                      lang: langue,
                    }
                  : undefined
              }
              inventoryNumber={selection.data.oeuvre.numeroInventaire}
            />
            <dl className={styles.details}>
              <div>
                <dt>{t('regie:oeuvres.colonneStatut')}</dt>
                <dd>
                  <PastilleStatut statut={selection.data.oeuvre.statut} />
                </dd>
              </div>
              <div>
                <dt>{t('regie:oeuvres.colonnePrix')}</dt>
                <dd>
                  {selection.data.oeuvre.prixPublicCentimes === null ? (
                    '—'
                  ) : (
                    <Price
                      cents={selection.data.oeuvre.prixPublicCentimes}
                      lang={langue}
                      currency={selection.data.oeuvre.devise}
                      size="lg"
                    />
                  )}
                </dd>
              </div>
              <div>
                <dt>{t('regie:oeuvres.localisation')}</dt>
                <dd>{selection.data.localisation?.nom ?? '—'}</dd>
              </div>
            </dl>
            <Link
              className={styles.lienFiche}
              to={`/${galerieSlug}/oeuvres/${selection.data.oeuvre.id}`}
            >
              {t('regie:oeuvres.voirFicheComplete')}
            </Link>
          </aside>
        ) : null}
      </div>

      <NouvelleOeuvreDialog
        registrePoliceActif={galerieAModule(galerie, 'registre-police')}
        // Remonte le dialogue à chaque ouverture : formulaire toujours vierge,
        // sans effet de réinitialisation (React ne tolère plus le
        // `setState` synchrone dans un effet).
        key={nouvelleOuverte ? 'nouvelle-ouverte' : 'nouvelle-fermee'}
        galerieSlug={galerieSlug}
        open={nouvelleOuverte && peutCreer}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermerNouvelle();
        }}
        onCreee={(id) => {
          fermerNouvelle();
          navigate(`/${galerieSlug}/oeuvres/${id}`);
        }}
      />

      <ImportExportDialog
        galerieSlug={galerieSlug}
        open={importExportOuvert}
        onOpenChange={setImportExportOuvert}
        filtres={filtres}
      />
    </div>
  );
}
